import dayjs, { Dayjs } from 'dayjs'
import React, { useCallback, useEffect, useId, useMemo, useState, useRef, useContext } from 'react'
import Slider from 'react-slick'
import { Button } from '@notino/react-styleguide'
import { Portal } from 'react-portal'

// hooks
import { useIntl } from 'react-intl'
import useMessages from '../../hooks/useMessages'

// types
import { DateCellData, DateCellsData, DateTimeSlotPickerProps, GetDateCellsDataArgs, SliderMonth, TimeSlotValueType } from './types'

// utils
import { DEFAULT_DATE_FORMAT, FULL_MONTH_NAME } from '../../utils/enums'
import { AppContext } from '../../utils/appProvider'

// components
import CustomRadioButtons from '../CustomRadioButtons/CustomRadioButtons'
import Modal from '../Modal/Modal'

// styles
import * as SC from './DateTimeSlotPickerStyles'

const getWeekDays = () => {
	const firstDayOfWeek = dayjs().localeData().firstDayOfWeek()
	const weekdays = dayjs().localeData().weekdaysMin()
	return [...weekdays.slice(firstDayOfWeek), ...weekdays.slice(0, firstDayOfWeek)]
}

const SLIDER_MONTHS_COUNT_BEFORE_CURRENT = 6 // half a year
const SLIDER_MONTHS_COUNT_AFTER_CURRENT = 24 // 2 years

const getSliderMonths = (selectedYear: number, selectedMonth: number): SliderMonth[] => {
	const selectedDate = dayjs(new Date(selectedYear, selectedMonth, 1))
	const today = dayjs()
	const startDate = today.subtract(SLIDER_MONTHS_COUNT_BEFORE_CURRENT, 'month')
	const slidesCount = SLIDER_MONTHS_COUNT_BEFORE_CURRENT + SLIDER_MONTHS_COUNT_AFTER_CURRENT

	const slides: SliderMonth[] = []

	for (let i = 0; i < slidesCount; i += 1) {
		const date = startDate.add(i, 'month')
		const slide: SliderMonth = {
			label: date.format(FULL_MONTH_NAME),
			date: date.format(DEFAULT_DATE_FORMAT),
			isActive: date.isSame(selectedDate, 'month'),
			disabled: date.isBefore(today, 'month')
		}
		slides.push(slide)
	}

	return slides
}

const getDateCellsData = (data: GetDateCellsDataArgs): DateCellsData => {
	const { selectedMonth, selectedYear, startDate, endDate, responseData } = data

	const startOfTheMonth = dayjs(new Date(selectedYear, selectedMonth, 1))
	const endOfMonth = startOfTheMonth.endOf('month')

	const endDayDayOfTheMonth = endOfMonth.date()

	const startDayOfTheWeek = startOfTheMonth.weekday()

	const dateCellsData: DateCellData[] = [...Array(endDayDayOfTheMonth).keys()].map((index) => {
		const day = index + 1
		const date = dayjs(new Date(selectedYear, selectedMonth, day)).startOf('day')
		const isAvailable = responseData
			? !date.isBefore(dayjs(), 'date') && !!responseData?.find((d) => dayjs(d.date).isSame(date, 'date'))?.available
			: !date.isBefore(dayjs(), 'date')

		const isRange = startDate && endDate
		const startOfTheRange = !!startDate?.isSame(date, 'date')
		const endOfTheRange = !!endDate?.isSame(date, 'date')

		return {
			isAvailable,
			isSelected: startOfTheRange || endOfTheRange,
			isStartOfTheRange: !!(isRange && startOfTheRange),
			isEndOfTheRange: !!(isRange && endOfTheRange),
			isBetweenRanges: !!(startDate && endDate && date.isAfter(startDate, 'date') && date.isBefore(endDate, 'date')),
			isToday: date.isSame(dayjs(), 'date'),
			date,
			day
		}
	})

	return {
		// weekDays are zero based, but grid axis starts with 1
		gridShift: startDayOfTheWeek + 1,
		data: dateCellsData
	}
}

const getInitialFocusedDay = (cells: DateCellData[], startDate: Dayjs | undefined): number => {
	if (startDate) {
		const selectedInMonth = cells.find((cell) => cell.date.isSame(startDate, 'date'))
		if (selectedInMonth) {
			return selectedInMonth.day
		}
	}

	const todayCell = cells.find((cell) => cell.isToday)
	if (todayCell) {
		return todayCell.day
	}

	const firstAvailable = cells.find((cell) => cell.isAvailable)
	return firstAvailable?.day ?? cells[0]?.day ?? 1
}

const clampDayInMonth = (year: number, month: number, day: number) => {
	const daysInMonth = dayjs(new Date(year, month, 1)).daysInMonth()
	return Math.min(day, daysInMonth)
}

// TODO: find better solution for swiper
// react-slick is not maintained anymore and causes a lot of troubles and workarounds
const DateTimeSlotPicker = <TimeSlotValue extends string = TimeSlotValueType>(props: DateTimeSlotPickerProps<TimeSlotValue>) => {
	const {
		value,
		onChange,
		availableDates,
		disabled,
		loading,
		onChangeView,
		onChangeDayPeriod,
		maxRange,
		timeSlotOptions,
		size = 'small',
		layout = 'col',
		calendarErrorMsg
	} = props
	const { dateFrom, dateTo, timeSlot } = value

	const { messages } = useMessages()
	const { formatMessage, formatDate } = useIntl()
	const { assetsPath } = useContext(AppContext)
	const monthLabelId = useId()
	const calendarErrorId = useId()

	const [popup, setPopup] = useState<{ open: boolean; handler?: () => void }>({ open: false })

	const now = dayjs()

	let startDate: Dayjs | undefined
	let endDate: Dayjs | undefined

	if (dateFrom && dateTo) {
		if (dateFrom.isBefore(dateTo, 'date')) {
			startDate = dateFrom
			endDate = dateTo
		} else if (dateFrom.isSame(dateTo, 'date')) {
			startDate = dateFrom
		} else {
			startDate = dateTo
			endDate = dateFrom
		}
	} else {
		startDate = dateFrom || dateTo
	}

	const [selectedYear, setSelectedYear] = useState(startDate?.year() || now.year())
	const [selectedMonth, setSelectedMonth] = useState(startDate?.month() || now.month())
	const selectedDate = dayjs(new Date(selectedYear, selectedMonth, 1))

	const sliderRef = useRef<Slider | null>(null)
	const prevMonthButtonRef = useRef<HTMLButtonElement | null>(null)
	const nextMonthButtonRef = useRef<HTMLButtonElement | null>(null)
	const monthButtonToRestoreRef = useRef<'prev' | 'next' | null>(null)
	const dayButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
	const pendingMonthNavRef = useRef<{ day: number; moveFocus: boolean } | null>(null)
	const hasMountedMonthRef = useRef(false)
	const [monthAnnouncement, setMonthAnnouncement] = useState('')

	const startDateString = startDate ? dayjs(startDate).format(DEFAULT_DATE_FORMAT) : undefined

	useEffect(() => {
		const newStartDate = dayjs(startDateString)
		setSelectedYear(newStartDate.year())
		setSelectedMonth(newStartDate.month())
	}, [startDateString])

	const sliderMonths = getSliderMonths(selectedYear, selectedMonth)
	const weekDays = getWeekDays()
	const dayCellsData = getDateCellsData({ selectedMonth, selectedYear, startDate, endDate, responseData: availableDates })

	const [focusedDay, setFocusedDay] = useState(() => getInitialFocusedDay(dayCellsData.data, startDate))

	const activeSlideIndex = sliderMonths.findIndex((slide) => slide.isActive)
	const todaySlideIndex = sliderMonths.findIndex((slide) => dayjs(slide.date).isSame(dayjs(), 'month'))
	const isLastSlide = sliderMonths.length - 1 === activeSlideIndex
	const isFirstAvailableSlide = selectedDate.isSame(dayjs(), 'month')

	const monthYearLabel = useMemo(() => {
		return formatDate(selectedDate.toDate(), { month: 'long', year: 'numeric' })
	}, [formatDate, selectedDate])

	const focusDayButton = useCallback((day: number) => {
		const button = dayButtonRefs.current.get(day)
		button?.focus()
	}, [])

	useEffect(() => {
		if (pendingMonthNavRef.current != null) {
			const { day: pendingDay, moveFocus } = pendingMonthNavRef.current
			const day = clampDayInMonth(selectedYear, selectedMonth, pendingDay)
			pendingMonthNavRef.current = null
			setFocusedDay(day)
			if (moveFocus) {
				requestAnimationFrame(() => focusDayButton(day))
			}
			return
		}

		const daysInMonth = dayCellsData.data.length
		if (focusedDay > daysInMonth) {
			setFocusedDay(daysInMonth)
		}
	}, [selectedMonth, selectedYear, dayCellsData.data.length, focusedDay, focusDayButton])

	useEffect(() => {
		if (!hasMountedMonthRef.current) {
			hasMountedMonthRef.current = true
			return
		}
		setMonthAnnouncement(monthYearLabel)
	}, [monthYearLabel])

	const handleChangeSlide = (newIndex: number) => {
		const newSlide = sliderMonths[newIndex]

		const newDate = dayjs(newSlide.date)
		const newYear = newDate.year()
		const newMonth = newDate.month()

		setSelectedYear(newYear)
		setSelectedMonth(newMonth)

		if (onChangeView) {
			onChangeView(newYear, newMonth)
		}

		// react-slick (and disabling the edge month button) can drop focus; put it back on a month control
		const buttonToRestore = monthButtonToRestoreRef.current
		if (buttonToRestore) {
			monthButtonToRestoreRef.current = null
			requestAnimationFrame(() => {
				const preferred = buttonToRestore === 'prev' ? prevMonthButtonRef.current : nextMonthButtonRef.current
				const fallback = buttonToRestore === 'prev' ? nextMonthButtonRef.current : prevMonthButtonRef.current

				if (preferred && !preferred.disabled) {
					preferred.focus()
					return
				}

				if (fallback && !fallback.disabled) {
					fallback.focus()
				}
			})
		}
	}

	const goToSlideIndex = (newIndex: number, options?: { day?: number; moveFocus?: boolean }) => {
		if (newIndex < todaySlideIndex || newIndex >= sliderMonths.length) {
			return
		}

		if (options?.day != null) {
			pendingMonthNavRef.current = {
				day: options.day,
				moveFocus: options.moveFocus ?? true
			}
		}

		sliderRef.current?.slickGoTo(newIndex)
	}

	const isInteractionBlocked = Boolean(disabled || loading)

	const handleChangeNextSlide = () => {
		if (isInteractionBlocked) {
			return
		}
		monthButtonToRestoreRef.current = 'next'
		goToSlideIndex(activeSlideIndex + 1, { day: focusedDay, moveFocus: false })
	}

	const handleChangePrevSlide = () => {
		if (isInteractionBlocked) {
			return
		}
		monthButtonToRestoreRef.current = 'prev'
		goToSlideIndex(activeSlideIndex - 1, { day: focusedDay, moveFocus: false })
	}

	const handleChangeDate = (newDate: Dayjs, isAvailable: boolean) => {
		if (!isAvailable || isInteractionBlocked) {
			return
		}

		let newStartDate: Dayjs | undefined
		let newEndDate: Dayjs | undefined

		if (startDate && endDate) {
			newStartDate = newDate
			newEndDate = undefined
		} else if (startDate) {
			if (newDate.isBefore(startDate, 'date')) {
				newStartDate = newDate
				newEndDate = startDate
			} else if (!newDate.isSame(startDate, 'date')) {
				newStartDate = startDate
				newEndDate = newDate
			}
		} else {
			newStartDate = newDate
		}

		if (newStartDate && newEndDate && maxRange !== undefined && !(newEndDate.diff(newStartDate, 'day') <= maxRange)) {
			newEndDate = newStartDate.add(maxRange, 'day')
			setPopup({
				open: true,
				handler: () => {
					onChange({
						timeSlot,
						dateFrom: newStartDate,
						dateTo: newEndDate
					})
				}
			})
		} else {
			onChange({
				timeSlot,
				dateFrom: newStartDate,
				dateTo: newEndDate
			})
		}
	}

	const handleChangeDayPeriod = (newPeriod: TimeSlotValue) => {
		if (isInteractionBlocked) {
			return
		}
		onChange({
			timeSlot: newPeriod,
			dateFrom,
			dateTo
		})
		if (onChangeDayPeriod) {
			onChangeDayPeriod(newPeriod, selectedYear, selectedMonth)
		}
	}

	useEffect(() => {
		const slickTrack = document.querySelector('.slick-track')
		const slides: NodeListOf<HTMLDivElement> | undefined = slickTrack?.querySelectorAll('[data-index]')
		slides?.forEach((slide) => {
			const dataIndexValue = slide.dataset.index

			if (Number(dataIndexValue) < todaySlideIndex) {
				// eslint-disable-next-line no-param-reassign
				slide.style.pointerEvents = 'none'
			} else {
				// eslint-disable-next-line no-param-reassign
				slide.style.pointerEvents = 'all'
			}
		})
	}, [selectedMonth, selectedYear, todaySlideIndex])

	const moveFocusByDays = (delta: number) => {
		const currentDate = dayjs(new Date(selectedYear, selectedMonth, focusedDay))
		const nextDate = currentDate.add(delta, 'day')
		const todayStart = dayjs().startOf('day')

		if (nextDate.isBefore(todayStart, 'month')) {
			return
		}

		if (nextDate.isSame(currentDate, 'month')) {
			setFocusedDay(nextDate.date())
			requestAnimationFrame(() => focusDayButton(nextDate.date()))
			return
		}

		const targetSlideIndex = sliderMonths.findIndex((slide) => dayjs(slide.date).isSame(nextDate, 'month'))
		if (targetSlideIndex === -1) {
			return
		}

		goToSlideIndex(targetSlideIndex, { day: nextDate.date(), moveFocus: true })
	}

	const handleGridKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (isInteractionBlocked) {
			return
		}

		const { key } = event

		if (key === 'ArrowLeft') {
			event.preventDefault()
			moveFocusByDays(-1)
			return
		}

		if (key === 'ArrowRight') {
			event.preventDefault()
			moveFocusByDays(1)
			return
		}

		if (key === 'ArrowUp') {
			event.preventDefault()
			moveFocusByDays(-7)
			return
		}

		if (key === 'ArrowDown') {
			event.preventDefault()
			moveFocusByDays(7)
			return
		}

		if (key === 'Home') {
			event.preventDefault()
			const currentDate = dayjs(new Date(selectedYear, selectedMonth, focusedDay))
			let target = currentDate.startOf('week')
			if (target.isBefore(dayjs(), 'date')) {
				target = dayjs().startOf('day')
			}
			if (target.isSame(currentDate, 'month')) {
				setFocusedDay(target.date())
				requestAnimationFrame(() => focusDayButton(target.date()))
			} else {
				const targetSlideIndex = sliderMonths.findIndex((slide) => dayjs(slide.date).isSame(target, 'month'))
				goToSlideIndex(targetSlideIndex, { day: target.date(), moveFocus: true })
			}
			return
		}

		if (key === 'End') {
			event.preventDefault()
			const currentDate = dayjs(new Date(selectedYear, selectedMonth, focusedDay))
			const endOfWeek = currentDate.endOf('week')
			if (endOfWeek.isSame(currentDate, 'month')) {
				setFocusedDay(endOfWeek.date())
				requestAnimationFrame(() => focusDayButton(endOfWeek.date()))
			} else {
				const targetSlideIndex = sliderMonths.findIndex((slide) => dayjs(slide.date).isSame(endOfWeek, 'month'))
				goToSlideIndex(targetSlideIndex, { day: endOfWeek.date(), moveFocus: true })
			}
			return
		}

		if (key === 'PageUp') {
			event.preventDefault()
			goToSlideIndex(activeSlideIndex - (event.shiftKey ? 12 : 1), { day: focusedDay, moveFocus: true })
			return
		}

		if (key === 'PageDown') {
			event.preventDefault()
			goToSlideIndex(activeSlideIndex + (event.shiftKey ? 12 : 1), { day: focusedDay, moveFocus: true })
		}
	}

	const handleSubmitPopup = () => {
		if (popup.handler) {
			popup.handler()
		}
		setPopup({ open: false })
	}

	const modalTitle = formatMessage(
		{ id: 'The selected range exceeds { daysCount }', defaultMessage: 'The selected range exceeds { daysCount }' },
		{ daysCount: maxRange }
	)
	const modalContent = formatMessage(
		{
			id: 'Selected time range must be shorter than { daysCount } days.',
			defaultMessage: 'Selected time range must be shorter than { daysCount } days.'
		},
		{ daysCount: maxRange }
	)

	return (
		<>
			{/**
			 * We need to create a new portal for this modal to fix issues with rendering in the Drawer component.
			 * Drawers consist of header, main content, and footer sections - the header and footer have a higher z-index than the main content.
			 * This component is used in the main content, which causes the drawer's header and footer elements to appear above the modal.
			 */}
			{popup.open && (
				<Portal>
					<Modal show onClose={handleSubmitPopup} ariaLabel={modalTitle} header={modalTitle}>
						<SC.ModalMessage>{modalContent}</SC.ModalMessage>
						<Button fullWidth onClick={handleSubmitPopup}>
							{messages['I understand']}
						</Button>
					</Modal>
				</Portal>
			)}
			<SC.DateTimeSlotPickerLayout $layout={layout}>
				<SC.DateTimeSlotPickerWrapper>
					<SC.DateTimeSlotPickerHeader>
						<SC.Year>{selectedYear}</SC.Year>
						<SC.MonthLabel id={monthLabelId}>{monthYearLabel}</SC.MonthLabel>
						<SC.MonthLiveRegion role={'status'} aria-live={'polite'} aria-atomic={'true'}>
							{monthAnnouncement}
						</SC.MonthLiveRegion>
						<SC.SliderWrapper>
							<SC.SliderButton
								ref={prevMonthButtonRef}
								type={'button'}
								$assetsPath={assetsPath}
								onClick={handleChangePrevSlide}
								disabled={isFirstAvailableSlide || disabled}
								aria-disabled={loading && !isFirstAvailableSlide && !disabled ? true : undefined}
								aria-label={formatMessage({ id: 'Previous month', defaultMessage: 'Previous month' })}
							/>
							<div aria-hidden={'true'}>
								<Slider
									ref={sliderRef}
									infinite={false}
									centerMode
									focusOnSelect={false}
									accessibility={false}
									speed={100}
									variableWidth
									slidesToScroll={1}
									autoplay={false}
									arrows={false}
									draggable={false}
									afterChange={handleChangeSlide}
									initialSlide={activeSlideIndex}
								>
									{sliderMonths.map(({ label, isActive, disabled: monthDisabled }, index) => {
										return (
											<SC.SliderItem key={index} $isActive={isActive} $isDisabled={monthDisabled || disabled || loading} $size={size}>
												{label}
											</SC.SliderItem>
										)
									})}
								</Slider>
							</div>
							<SC.SliderButton
								ref={nextMonthButtonRef}
								type={'button'}
								$assetsPath={assetsPath}
								$isNext
								onClick={handleChangeNextSlide}
								disabled={isLastSlide || disabled}
								aria-disabled={loading && !isLastSlide && !disabled ? true : undefined}
								aria-label={formatMessage({ id: 'Next month', defaultMessage: 'Next month' })}
							/>
						</SC.SliderWrapper>
					</SC.DateTimeSlotPickerHeader>
					<SC.CalendarWrapper>
						<SC.CalendarGrid
							role={'grid'}
							aria-labelledby={monthLabelId}
							aria-describedby={calendarErrorMsg ? calendarErrorId : undefined}
							onKeyDown={handleGridKeyDown}
						>
							{weekDays.map((day, index) => (
								<SC.HeaderCell $size={size} key={index}>
									{day}
								</SC.HeaderCell>
							))}
							{dayCellsData.data.map(({ day, date, isAvailable, isBetweenRanges, isEndOfTheRange, isStartOfTheRange, isSelected, isToday }) => {
								const isFocusedDay = day === focusedDay
								return (
									<SC.DayCell
										key={day}
										$gridShift={day === 1 ? dayCellsData.gridShift : undefined}
										$isStartOfTheRange={isStartOfTheRange}
										$isEndOfTheRange={isEndOfTheRange}
										$isBetweenRanges={isBetweenRanges}
										$size={size}
									>
										<SC.DayCellButton
											ref={(node) => {
												if (node) {
													dayButtonRefs.current.set(day, node)
												} else {
													dayButtonRefs.current.delete(day)
												}
											}}
											type={'button'}
											$isAvailable={isAvailable}
											$isSelected={isSelected}
											$isToday={isToday}
											$size={size}
											tabIndex={isFocusedDay ? 0 : -1}
											aria-selected={isSelected}
											aria-disabled={!isAvailable || disabled || loading}
											aria-label={formatDate(date.toDate(), {
												weekday: 'long',
												day: 'numeric',
												month: 'long',
												year: 'numeric'
											})}
											data-datepicker-focused-day={isFocusedDay ? 'true' : undefined}
											onClick={() => {
												setFocusedDay(day)
												handleChangeDate(date, isAvailable)
											}}
											onFocus={() => setFocusedDay(day)}
											onKeyDown={(event) => {
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault()
													handleChangeDate(date, isAvailable)
												}
											}}
										>
											{day}
										</SC.DayCellButton>
									</SC.DayCell>
								)
							})}
						</SC.CalendarGrid>
					</SC.CalendarWrapper>
					{calendarErrorMsg && (
						<SC.ErrorMsg id={calendarErrorId} role={'alert'}>
							{calendarErrorMsg}
						</SC.ErrorMsg>
					)}
				</SC.DateTimeSlotPickerWrapper>
				<SC.RadioButtonsWrapper>
					<CustomRadioButtons<TimeSlotValue>
						label={messages['Select time']}
						labelVisuallyHidden={layout === 'row'}
						value={timeSlot}
						onChange={(newPeriod) => handleChangeDayPeriod(newPeriod)}
						name={'time-slots'}
						options={timeSlotOptions}
						disabled={disabled}
						loading={loading}
						size={size}
					/>
				</SC.RadioButtonsWrapper>
			</SC.DateTimeSlotPickerLayout>
		</>
	)
}

export default DateTimeSlotPicker
